import React, { useEffect, useState, useRef } from 'react';
import * as API from './api.js';
import { Loader2, Calendar, ChevronDown } from 'lucide-react';
import FinancialYear from "./assets/Financial_Year.svg"

const FinancialCycles = ({ onSetFinancialCycle }) => {
    const [financialCycles, setFinancialCycles] = useState([]);
    const [selectedCycle, setSelectedCycle] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [hoverIndex, setHoverIndex] = useState(null);
    const selectedItemRef = useRef(null);

    const appendDateRangesToFinancialYears = (financialYears = []) => {
        const now = new Date();
        const todayStart = new Date(now);
        todayStart.setHours(0, 0, 0, 0);
        const todayEnd = new Date(now);
        todayEnd.setHours(23, 59, 59, 999);

        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        const endOfWeek = new Date(now);
        endOfWeek.setDate(now.getDate() + (6 - now.getDay()));
        endOfWeek.setHours(23, 59, 59, 999);

        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        startOfMonth.setHours(0, 0, 0, 0);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        endOfMonth.setHours(23, 59, 59, 999);

        const formatDate = (dateStr) => {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
            });
        };

        const normalizedFinancialYears = financialYears.map(fy => {
            const startDateISO = new Date(fy.startDate).toISOString();
            const endDateISO = new Date(fy.endDate).toISOString();
            return {
                label: `${formatDate(startDateISO)} – ${formatDate(endDateISO)}`,
                startDate: startDateISO,
                endDate: endDateISO
            };
        });

        const allOption = {
            label: "All",
            startDate: null,
            endDate: null
        };

        return [
            allOption,
            {
                label: `Today (${formatDate(todayStart)} – ${formatDate(todayEnd)})`,
                startDate: todayStart.toISOString(),
                endDate: todayEnd.toISOString()
            },
            {
                label: `This Week (${formatDate(startOfWeek)} – ${formatDate(endOfWeek)})`,
                startDate: startOfWeek.toISOString(),
                endDate: endOfWeek.toISOString()
            },
            {
                label: `This Month (${formatDate(startOfMonth)} – ${formatDate(endOfMonth)})`,
                startDate: startOfMonth.toISOString(),
                endDate: endOfMonth.toISOString()
            },
            ...normalizedFinancialYears
        ];
    };

    const getFinancialCycles = async () => {
        try {
            const { financialYears } = await API.getFinancialYears();
            const combinedCycles = appendDateRangesToFinancialYears(financialYears);

            let stored = null;
            try {
                stored = JSON.parse(localStorage.getItem("selectedFinancialCycle"));
            } catch (e) { stored = null; }

            let defaultCycle = null;
            if (stored && stored.label) {
                defaultCycle = combinedCycles.find(c =>
                    c.label === stored.label &&
                    c.startDate === stored.startDate &&
                    c.endDate === stored.endDate
                );
            }
            if (!defaultCycle) {
                defaultCycle = combinedCycles[combinedCycles.length - 1] || combinedCycles.find(c => c.label.startsWith("Today"));
            }

            setSelectedCycle(defaultCycle);
            setFinancialCycles(combinedCycles);
            onSetFinancialCycle(defaultCycle);
        } catch (error) {
            console.error('Error fetching financial cycles:', error);
        }
    };

    const handleOptionSelect = (cycle) => {
        setSelectedCycle(cycle);
        onSetFinancialCycle(cycle);
        setIsOpen(false);
        localStorage.setItem("selectedFinancialCycle", JSON.stringify({
            label: cycle.label,
            startDate: cycle.startDate,
            endDate: cycle.endDate
        }));
    };

    const toggleDropdown = () => setIsOpen(!isOpen);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        getFinancialCycles();
    }, []);

    // Scroll selected item into view when dropdown opens
    useEffect(() => {
        if (isOpen && selectedItemRef.current) {
            selectedItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [isOpen]);

    if (financialCycles.length === 0) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1rem'
            }}>
                <Loader2 style={{
                    width: '2rem',
                    height: '2rem',
                    color: '#2563eb',
                    animation: 'spin 1s linear infinite'
                }} />
            </div>
        );
    }

    return (
        <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h2 style={{ marginBottom: 0, fontSize: 18, fontWeight: 600, color: "#081425" }}>{selectedCycle?.label}</h2>
            <div style={{ position: 'relative', width: '100%', maxWidth: 'fit-content', minWidth: "18rem" }} ref={dropdownRef}>
                <button
                    onClick={toggleDropdown}
                    style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.4rem 1rem',
                        backgroundColor: 'white',
                        border: '2px solid #C9C9C9',
                        borderRadius: '0.5rem',
                        transition: 'border-color 0.2s'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <img src={FinancialYear} alt='FinancialYear' style={{ width: 20, height: 20, color: '#2563eb' }} />
                        <span style={{ color: '#374151', fontWeight: 500 }}>
                            {selectedCycle ? selectedCycle.label : 'Select financial year'}
                        </span>
                    </div>
                    <ChevronDown
                        style={{
                            width: 20,
                            height: 20,
                            color: '#6b7280',
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s'
                        }}
                    />
                </button>

                {isOpen && (
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: '0.25rem',
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
                        zIndex: 50,
                        maxHeight: '16rem',
                        overflowY: 'auto'
                    }}>
                        {financialCycles.map((cycle, idx) => {
                            const isSelected = selectedCycle?.label === cycle.label;
                            const isHovered = hoverIndex === idx;

                            return (
                                <button
                                    key={idx}
                                    ref={isSelected ? selectedItemRef : null} // Attach ref to selected item
                                    onClick={() => handleOptionSelect(cycle)}
                                    onMouseEnter={() => setHoverIndex(idx)}
                                    onMouseLeave={() => setHoverIndex(null)}
                                    style={{
                                        width: '100%',
                                        textAlign: 'left',
                                        padding: '0.4rem 1rem',
                                        borderLeft: '4px solid',
                                        borderLeftColor: isSelected ? '#3b82f6' : 'transparent',
                                        backgroundColor: isHovered
                                            ? '#eff6ff'
                                            : 'white',
                                        color: "#081425",
                                        transition: 'background-color 0.2s, border-left-color 0.2s',
                                        borderRadius: "4px",
                                        marginBottom: idx !== financialCycles.length - 1 ? '1px' : '0',
                                        fontWeight: isSelected ? 600 : 400,
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: '0.875rem',
                                        }}
                                    >
                                        {cycle.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>

    );
};

export default FinancialCycles;